import React, { ComponentType, useCallback, useEffect, useMemo, useState } from 'react'
import {
  CustomDropdownItemOption,
  DropDownItemOptionComponentProps,
  SelectDropdown,
  SelectOption,
} from '../../../../../../../components/SelectDropdown/SelectDropdown'
import classNames from 'classnames'
import { useAppDispatch, useAppSelector } from '../../../../../../../store/hooks'
import {
  selectMeetingSummaryAgentEditing,
  selectMeetingSummaryAgentLoadingInteraction,
  setMeetingSummaryAgentLoadingInteraction,
  setMeetingSummaryEditing,
  setMeetingSummaryElements,
  setMeetingSummaryFindAndReplaceLoading,
} from '../../../../../../../store/meetingSummaryAgent/meetingSummaryAgent'
import { TrackEvent, useTracking } from '../../../../common/tracking'
import { Modal } from '../../../../../../../components/Modal/Modal'
import { Button } from '../../../../../../../components/Button/Button'
import { FindAndReplaceModal } from '../../../FindAndReplaceModal/FindAndReplaceModal'
import { addToast } from '../../../../../../../store/toast/toastSlice'
import { FindAndReplaceResponse, useFindAndReplaceMutation } from '../../../../../../../store/meetingSummary/findAndReplace'
import { parseMeetingSummary } from '../../utils'
import { selectSelectedMeetingTemplate } from '../../../../../../../store/meetingSummary/meetingTemplates'
import { useMeetingSummary } from '../../hooks'
import { selectAccountId } from '../../../../../../../store/auth/authSlice'
import { useParams } from 'react-router-dom'
import { selectRoomSession, selectRoomSessionHasTranscription, updateRoomSessionMetadata } from '../../../../../../../store/meetingSummary/roomSession'
import { parseMetadata } from '../../../utils'
import { Tooltip } from '../../../../../../../components/Tooltip/Tooltip'

interface MeetingSummaryAgentDropdownOption {
  icon?: string
  IconComponent?: ComponentType
  selectOption: SelectOption
  handleSelected: (option: SelectOption) => void | Promise<void>
}

export interface MeetingSummaryAgentDropdownButtonProps {
  executeMeetingSummary: () => Promise<void>
}

export const MeetingSummaryAgentDropdownButton: React.FC<MeetingSummaryAgentDropdownButtonProps> = ({ executeMeetingSummary }) => {
  const dispatch = useAppDispatch()
  const { tracked } = useTracking()

  const hasTranscription = useAppSelector(selectRoomSessionHasTranscription)
  const meetingSummaryAgentLoadingInteraction = useAppSelector(selectMeetingSummaryAgentLoadingInteraction)
  const editing = useAppSelector(selectMeetingSummaryAgentEditing)

  const [showRerunConfirmationModal, setShowRerunConfirmationModal] = useState<boolean>(false)

  const [triggerFindAndReplace, { isSuccess: isFindAndReplaceSuccess, data: findAndReplaceResponse }] = useFindAndReplaceMutation()

  const [showFindAndReplaceModal, setShowFindAndReplaceModal] = useState<boolean>(false)
  const selectedMeetingTemplate = useAppSelector(selectSelectedMeetingTemplate)
  const roomSession = useAppSelector(selectRoomSession)
  const { refetchAiResults, refetchMeetingTasks } = useMeetingSummary()
  const accountId = useAppSelector(selectAccountId)
  const { roomId = '', sessionId = '' } = useParams()

  useEffect(() => {
    const refreshContent = async ({
      changeCount,
      newMeetingSummary,
      newTitle,
      aiResultChangeCount,
      taskChangeCount,
      roomTitleChangeCount,
    }: FindAndReplaceResponse) => {
      setShowFindAndReplaceModal(false)

      if (changeCount === 0) {
        dispatch(setMeetingSummaryFindAndReplaceLoading(false))
        return
      }

      if (newMeetingSummary) {
        dispatch(setMeetingSummaryElements(parseMeetingSummary(newMeetingSummary)))
      }

      if (taskChangeCount > 0) {
        await refetchMeetingTasks()
      }

      if (aiResultChangeCount > 0) {
        await refetchAiResults()
      }

      if (roomTitleChangeCount > 0 && newTitle) {
        if (roomSession) {
          const currentMetadata = parseMetadata(roomSession.metadata)
          const updatedMetadata = {
            ...currentMetadata,
            external: {
              ...currentMetadata.external,
              title: newTitle,
            },
          }

          const stringifiedMetadata = JSON.stringify(updatedMetadata)

          dispatch(updateRoomSessionMetadata(stringifiedMetadata))
        }
      }

      dispatch(setMeetingSummaryFindAndReplaceLoading(false))
    }

    if (!isFindAndReplaceSuccess || !findAndReplaceResponse) {
      return
    }

    dispatch(
      addToast({
        variant: 'success',
        title: 'Success',
        messages: [`${findAndReplaceResponse.changeCount} replacements made`],
      }),
    )

    refreshContent(findAndReplaceResponse)
  }, [isFindAndReplaceSuccess, findAndReplaceResponse, dispatch, refetchAiResults, roomSession, refetchMeetingTasks])

  const _handleRerun = useCallback(async () => {
    setShowRerunConfirmationModal(false)
    dispatch(setMeetingSummaryEditing(false))
    await executeMeetingSummary()
  }, [dispatch, executeMeetingSummary])

  const handleRerun = tracked(_handleRerun, { eventName: TrackEvent.MEETING_SUMMARY_AGENT_RERUN })

  const _handleFindAndReplace = useCallback(
    async (find: string, replace: string) => {
      dispatch(setMeetingSummaryFindAndReplaceLoading(true))
      const meetingTemplateId = selectedMeetingTemplate?.id ?? ''

      try {
        await triggerFindAndReplace({
          accountId,
          roomId,
          sessionId,
          payload: {
            meetingTemplateId,
            find,
            replace,
          },
        }).unwrap()
      } catch {
        dispatch(setMeetingSummaryAgentLoadingInteraction(null))
        setShowFindAndReplaceModal(false)
      }
    },
    [accountId, dispatch, roomId, selectedMeetingTemplate?.id, sessionId, triggerFindAndReplace],
  )

  const handleFindAndReplace = tracked(
    _handleFindAndReplace,
    {
      eventName: TrackEvent.MEETING_SUMMARY_FIND_AND_REPLACE,
      eventDetails: {
        roomId,
        sessionId,
      },
    },
    {
      eventDetailsExtractor: (find: string, replace: string) => {
        return { find, replace }
      },
    },
  )

  const options: MeetingSummaryAgentDropdownOption[] = useMemo(
    () => [
      {
        icon: 'bi-arrow-clockwise',
        selectOption: {
          id: 'rerun',
          label: 'Rerun',
          tooltipLabel: !hasTranscription ? 'Meeting has no transcription' : undefined,
          disabled: !!meetingSummaryAgentLoadingInteraction || !hasTranscription,
        },
        handleSelected: () => setShowRerunConfirmationModal(true),
      },
      {
        icon: 'bi-arrow-left-right',
        selectOption: {
          id: 'findAndReplace',
          label: 'Find and Replace',
          disabled: !!meetingSummaryAgentLoadingInteraction || editing,
        },
        handleSelected: () => setShowFindAndReplaceModal(true),
      },
    ],
    [hasTranscription, meetingSummaryAgentLoadingInteraction, editing],
  )

  const selectOptions = useMemo(() => options.map((o) => o.selectOption), [options])

  const handleOptionSelected = async (option: SelectOption) => {
    const { id: optionId } = option
    const dropdownOption = options.find((option) => option.selectOption.id === optionId)
    if (!dropdownOption) {
      return
    }
    const {
      handleSelected,
      selectOption: { disabled },
    } = dropdownOption
    if (disabled) {
      return
    }
    await handleSelected(option)
  }

  const MeetingSummaryAgentDropdownItemOption: React.FC<DropDownItemOptionComponentProps> = ({ optionId, tooltipLabel, optionLabel, disabled, onClick }) => {
    const option = options.find((resultOption) => resultOption.selectOption.id === optionId)
    if (!option) {
      return null
    }
    const { icon, IconComponent } = option

    return (
      <Tooltip title={tooltipLabel}>
        <CustomDropdownItemOption
          role='button'
          onClick={onClick}
          className={classNames('d-flex gap-2 align-items-center', { 'dropdown-item-disabled': disabled })}
        >
          {IconComponent ? <IconComponent /> : icon ? <i className={`bi ${icon}`} /> : null}
          {optionLabel}
        </CustomDropdownItemOption>
      </Tooltip>
    )
  }

  return (
    <>
      <div className='extra-dropdown-container d-none d-sm-block'>
        <SelectDropdown
          className='zocks-py-0.375 px-2'
          options={selectOptions}
          icon='bi-three-dots-vertical'
          iconOnly
          hideArrows
          DropDownItemOptionComponent={MeetingSummaryAgentDropdownItemOption}
          onOptionSelected={handleOptionSelected}
        />
      </div>
      {showRerunConfirmationModal ? (
        <RerunConfirmationModal show={showRerunConfirmationModal} onRerun={handleRerun} onHide={() => setShowRerunConfirmationModal(false)} />
      ) : null}
      {showFindAndReplaceModal ? (
        <FindAndReplaceModal
          show={showFindAndReplaceModal}
          handleFindAndReplaceClicked={handleFindAndReplace}
          onHide={() => setShowFindAndReplaceModal(false)}
        />
      ) : null}
    </>
  )
}

interface RerunConfirmationModalProps {
  show: boolean
  onRerun: () => void
  onHide: () => void
}

const RerunConfirmationModal: React.FC<RerunConfirmationModalProps> = ({ show, onRerun, onHide }) => {
  return (
    <Modal
      show={show}
      onHide={onHide}
      header={<></>}
      body={
        <div className='d-flex flex-column zocks-gap-3'>
          <h4 className='text-center'>Are you sure you want to rerun the meeting summary?</h4>
          <div className='paragraph-medium-extended'>
            If you rerun the summary, your previous edits will be lost. Rerun is only necessary in some limited cases, so please confirm before proceeding.
          </div>
        </div>
      }
      footer={
        <div className='d-flex flex-grow-1 gap-3 align-items-center'>
          <Button variant='secondary' className='flex-grow-1' onClick={onHide}>
            Cancel
          </Button>
          <Button variant='primary' className='flex-grow-1' onClick={onRerun}>
            Rerun
          </Button>
        </div>
      }
      centered={true}
    />
  )
}
